import { useCallback, useRef, useState } from 'react';
import {
  BULK_DOWNLOAD_GAP_MS,
  deliverInHiddenFrame,
  runBulkDownload,
  type BulkDownloadStatus,
} from '../bulkDownload';

export interface BulkDownload<T> {
  /** The current or last run; `null` before the first and after `dismiss`. */
  status: BulkDownloadStatus | null;
  /** Start a run. Ignored while one is in progress, so a double click cannot run two. */
  start: (targets: readonly T[]) => void;
  dismiss: () => void;
}

/**
 * React state over `runBulkDownload` — the Files browser and the Shared view
 * each hold one, with their own presign, and render it with
 * `BulkDownloadStatusLine`. All the rules are in `bulkDownload.ts`.
 */
export function useBulkDownload<T>(
  presign: (target: T) => Promise<string | undefined>,
  nameOf: (target: T) => string
): BulkDownload<T> {
  const [status, setStatus] = useState<BulkDownloadStatus | null>(null);
  const runningRef = useRef(false);

  const start = useCallback(
    (targets: readonly T[]) => {
      if (runningRef.current || targets.length === 0) return;
      runningRef.current = true;
      void runBulkDownload(targets, {
        nameOf,
        presign,
        deliver: (url) => deliverInHiddenFrame(url),
        onProgress: setStatus,
        pause: () => new Promise((resolve) => setTimeout(resolve, BULK_DOWNLOAD_GAP_MS)),
      }).finally(() => {
        runningRef.current = false;
      });
    },
    [presign, nameOf]
  );

  const dismiss = useCallback(() => {
    if (!runningRef.current) setStatus(null);
  }, []);

  return { status, start, dismiss };
}
